import { FC, useEffect, useState } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	Alert,
	Box,
	Button,
	ButtonDropdown,
	ColumnLayout,
	Container,
	ContentLayout,
	Grid,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'
import HeaderWithStatusBadges from '../../../components/Common/HeaderWithStatusBadges'
import LoadingSpinner from '../../../components/LoadingSpinner'
import ApplicationDetails from '../../../components/MongoDB/ApplicationDetails'
import ClusterSummary from '../../../components/MongoDB/ClusterSummary'
import RequestError, {
	getMongoDBErrorMessage
} from '../../../components/MongoDB/RequestError'
import ApprovalForm from '../../../components/MongoDB/ApprovalForm'
import RejectionForm from '../../../components/MongoDB/RejectionForm'
import CommentsOverview from '../../../components/MongoDB/CommentsOverview'
import useSingleMongoDB from '../../../hooks/query/useSingleMongoDB'
import useUserPermissions from '../../../hooks/query/useUserPermissions'
import useReopenMongoDB from '../../../hooks/mutate/useReopenMongoDB'
import hasRequiredPermissions from '../../../helper/hasRequiredPermissions'
import type { MongoDBRequest } from '../../../types/mongodb'

const MongoDBDetails: FC = () => {
	const { id = '' } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const routeNotice =
		(location.state as { notice?: string } | null)?.notice || ''
	const { authState } = useOktaAuth()
	const email = String(authState?.idToken?.claims.email || '')
	const requestQuery = useSingleMongoDB(id)
	const accessQuery = useUserPermissions(email)
	const { mutateAsync: reopenMongoDB, isLoading: isReopening } =
		useReopenMongoDB(id)
	const [notice, setNotice] = useState(routeNotice)
	const [submissionError, setSubmissionError] = useState('')
	const [decision, setDecision] = useState<{
		action: 'APPROVE' | 'REJECT'
		request: MongoDBRequest
	} | null>(null)

	useEffect(() => {
		setDecision(null)
		setSubmissionError('')
		setNotice(routeNotice)
	}, [id, routeNotice])

	// Keep the reviewed record and its timestamp together while a decision is open.
	const activeDecision = decision?.request.mongoDBID === id ? decision : null
	const request = activeDecision?.request || requestQuery.data
	const permissions = (accessQuery.data?.permissions || []).map((permission) =>
		permission.replace(/^mongodb:/i, 'mongodb:')
	)
	const refresh = () => {
		void requestQuery.refetch()
		void accessQuery.refetch()
	}
	const onSubmitted = () => {
		setDecision(null)
		setSubmissionError('')
		setNotice('Update submitted. Refresh to check the saved request status.')
	}

	if (requestQuery.isLoading || accessQuery.isLoading) return <LoadingSpinner />
	const error = requestQuery.error || accessQuery.error
	if (error) return <RequestError error={error} retry={refresh} />
	if (!request) return <Alert>MongoDB request was not found.</Alert>

	const readPermission = `mongodb:${request.mongoDBID}:read`
	const canApprove = hasRequiredPermissions(
		[readPermission, 'mongodb:*:approve'],
		permissions
	)
	const canCorrect = hasRequiredPermissions(
		[readPermission, `mongodb:${request.mongoDBID}:create`],
		permissions
	)
	const canDecide =
		canApprove &&
		request.status === 'PENDING_APPROVAL' &&
		!request.correctionPending
	const canEdit =
		canCorrect &&
		request.status === 'PENDING_APPROVAL' &&
		request.correctionPending
	const tribeDecision = request.approvalStatus.find(
		(item) => item.name === 'GCPTribe'
	)

	const reopen = async () => {
		if (isReopening) return
		setSubmissionError('')
		try {
			await reopenMongoDB({
				action: 'REOPEN',
				mongoDBID: request.mongoDBID
			})
			onSubmitted()
		} catch (cause) {
			setSubmissionError(getMongoDBErrorMessage(cause))
		}
	}

	return (
		<ContentLayout
			header={
				<HeaderWithStatusBadges
					headerName={`${request.applicationName} (${request.environment})`}
					status={request.status}
					navigateTo="/mongodb"
				/>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				{notice && <Alert type="info">{notice}</Alert>}
				{submissionError && <Alert type="error">{submissionError}</Alert>}
				<Grid
					gridDefinition={[
						{ colspan: { default: 8, xs: 8 } },
						{ colspan: { default: 4, xs: 4 } }
					]}
				>
					<SpaceBetween direction="vertical" size="l">
						<Container
							header={
								<Header
									variant="h2"
									actions={
										<SpaceBetween direction="horizontal" size="xs">
											<Button
												disabled={isReopening || Boolean(activeDecision)}
												onClick={refresh}
											>
												Refresh
											</Button>
											{canApprove && request.status === 'REJECTED' && (
												<Button
													loading={isReopening}
													onClick={() => void reopen()}
												>
													Reopen request
												</Button>
											)}
											{canEdit && (
												<Button
													disabled={isReopening}
													onClick={() => navigate(`/mongodb/${id}/reopen`)}
												>
													Edit corrections
												</Button>
											)}
											{canDecide && (
												<ButtonDropdown
													variant="primary"
													disabled={isReopening || Boolean(activeDecision)}
													items={[
														{ id: 'approve', text: 'Approve' },
														{ id: 'reject', text: 'Reject' }
													]}
													onItemClick={({ detail }) =>
														setDecision({
															action:
																detail.id === 'approve' ? 'APPROVE' : 'REJECT',
															request
														})
													}
												>
													Action
												</ButtonDropdown>
											)}
										</SpaceBetween>
									}
								>
									Request Details
								</Header>
							}
						>
							<ColumnLayout columns={2} variant="text-grid">
								{[
									['ID', request.mongoDBID],
									['Application', request.applicationName],
									['Environment', request.environment],
									['Requested by', request.requestorID],
									['Created', new Date(request.creationTime).toLocaleString()],
									[
										'Last updated',
										new Date(request.lastUpdatedTime).toLocaleString()
									]
								].map(([label, value]) => (
									<div key={label}>
										<Box variant="awsui-key-label">{label}</Box>
										<Box>{value}</Box>
									</div>
								))}
							</ColumnLayout>
						</Container>
						{request.correctionPending && (
							<Alert type="info">
								This request is awaiting saved corrections. Approval and
								rejection are unavailable until they are saved.
							</Alert>
						)}
						{canDecide && activeDecision?.action === 'APPROVE' && (
							<ApprovalForm
								request={activeDecision.request}
								onSubmitted={onSubmitted}
								onClose={() => setDecision(null)}
							/>
						)}
						{canDecide && activeDecision?.action === 'REJECT' && (
							<RejectionForm
								request={activeDecision.request}
								onSubmitted={onSubmitted}
								onClose={() => setDecision(null)}
							/>
						)}
						<ClusterSummary cluster={request.details.cluster} />
						<ApplicationDetails details={request.details} />
						<Container
							header={<Header variant="h2">GCP Tribe Decision</Header>}
						>
							<SpaceBetween direction="vertical" size="xs">
								<Box>Status: {tribeDecision?.status || 'Pending'}</Box>
								<Box>Decided by: {tribeDecision?.approverID || '-'}</Box>
								<Box>
									Decision time:{' '}
									{tribeDecision?.lastUpdatedTime
										? new Date(tribeDecision.lastUpdatedTime).toLocaleString()
										: '-'}
								</Box>
							</SpaceBetween>
						</Container>
					</SpaceBetween>
					<SpaceBetween direction="vertical" size="l">
						<Container header={<Header variant="h2">Rejection Reasons</Header>}>
							<CommentsOverview comments={request.comments} />
						</Container>
					</SpaceBetween>
				</Grid>
			</SpaceBetween>
		</ContentLayout>
	)
}

export default MongoDBDetails
